import { Clock, Lock, RotateCw, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { cn } from "@/lib/utils";

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

function formatLeaseCountdown(
  claimedAt: string | null,
  leaseExpiresAt: string | null,
): { text: string; isExpired: boolean } {
  if (!claimedAt) {
    return { text: "No active lock", isExpired: false };
  }
  const expiresMs = leaseExpiresAt
    ? new Date(leaseExpiresAt).getTime()
    : new Date(claimedAt).getTime() + TWO_HOURS_MS;
  const remainingMs = expiresMs - Date.now();
  if (remainingMs <= 0) {
    return { text: "Lease expired (2h elapsed)", isExpired: true };
  }
  const totalMinutes = Math.ceil(remainingMs / 60_000);
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return {
      text: `${hours}h ${mins}m left of 2h lock`,
      isExpired: false,
    };
  }
  return {
    text: `${totalMinutes}m left of 2h lock`,
    isExpired: false,
  };
}

export function AgentWorkLeasesTable({ projectId }: { projectId?: string }) {
  const utils = trpc.useUtils();
  const {
    data: workspaceRows,
    isLoading,
    isFetching,
    refetch,
  } = trpc.workRequest.listWorkspace.useQuery(undefined, {
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const rows = projectId
    ? (workspaceRows ?? []).filter((r) => r.projectId === projectId)
    : (workspaceRows ?? []);

  const claimedCount = rows.filter((r) => r.status === "claimed").length;
  const queuedCount = rows.filter((r) => r.status === "queued").length;

  const invalidateAll = (targetProjectId: string) => {
    utils.workRequest.listWorkspace.invalidate();
    utils.workRequest.list.invalidate({ projectId: targetProjectId });
    utils.workRequest.listForAgent.invalidate({ projectId: targetProjectId });
  };

  const releaseMutation = trpc.workRequest.releaseLease.useMutation({
    onSuccess: (_res, vars) => {
      toast.success("Agent lease lock released — request re-queued");
      invalidateAll(vars.projectId);
    },
    onError: (err) => toastTrpcError(err, "Failed to release agent lease"),
  });

  const cancelMutation = trpc.workRequest.cancel.useMutation({
    onSuccess: (_res, vars) => {
      toast.success("Work request cancelled");
      invalidateAll(vars.projectId);
    },
    onError: (err) => toastTrpcError(err, "Failed to cancel work request"),
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono tabular-nums text-foreground font-medium">
            {claimedCount} active {claimedCount === 1 ? "lock" : "locks"}
          </span>
          <span>·</span>
          <span className="font-mono tabular-nums">{queuedCount} queued</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RotateCw className={cn("h-3 w-3", isFetching && "animate-spin")} />
          Refresh queue
        </Button>
      </div>

      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[42%] whitespace-normal">
              Request / Target
            </TableHead>
            <TableHead className="w-[16%] whitespace-normal">Status</TableHead>
            <TableHead className="w-[26%] whitespace-normal">
              Claimed by / Lease
            </TableHead>
            <TableHead className="w-[16%] text-right whitespace-normal">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={4}
                className="py-8 text-center text-xs text-muted-foreground whitespace-normal"
              >
                No active agent work leases — 0 locks held across workspace
                projects
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const isClaimed = row.status === "claimed";
              const lease = formatLeaseCountdown(
                row.claimedAt,
                row.leaseExpiresAt,
              );
              const targetTitle =
                row.canonicalTitle ??
                row.scenarioName ??
                row.targetUrl ??
                "Work request";

              return (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-normal align-top">
                    <div className="space-y-0.5 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Badge
                          variant="outline"
                          className="shrink-0 rounded-sm font-mono text-2xs capitalize px-1.5 py-0"
                        >
                          {row.kind}
                        </Badge>
                        <span className="text-sm font-medium truncate">
                          {targetTitle}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {row.projectName}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-normal align-top">
                    {isClaimed ? (
                      <Badge
                        variant="default"
                        className="rounded-sm text-xs font-normal"
                      >
                        <Lock className="mr-1 h-3 w-3" />
                        Claimed
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="rounded-sm text-xs font-normal"
                      >
                        <Clock className="mr-1 h-3 w-3" />
                        Queued
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-normal align-top">
                    {isClaimed ? (
                      <div className="space-y-0.5">
                        <div className="text-xs font-medium text-foreground truncate">
                          {row.claimedBy
                            ? `${row.claimedBy}'s agent`
                            : "Autonomous agent"}
                        </div>
                        <div
                          className={cn(
                            "font-mono text-xs tabular-nums",
                            lease.isExpired
                              ? "text-destructive"
                              : "text-muted-foreground",
                          )}
                        >
                          {lease.text}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <div className="text-xs text-muted-foreground">
                          Unclaimed — waiting for pull
                        </div>
                        <div className="font-mono text-2xs tabular-nums text-muted-foreground truncate">
                          By {row.requestedBy || "teammate"}
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-normal align-top">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {isClaimed && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs gap-1"
                          disabled={
                            releaseMutation.isPending &&
                            releaseMutation.variables?.id === row.id
                          }
                          onClick={() =>
                            releaseMutation.mutate({
                              projectId: row.projectId,
                              id: row.id,
                            })
                          }
                        >
                          <Unlock className="h-3 w-3" />
                          Release lock
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={
                          cancelMutation.isPending &&
                          cancelMutation.variables?.id === row.id
                        }
                        onClick={() =>
                          cancelMutation.mutate({
                            projectId: row.projectId,
                            id: row.id,
                          })
                        }
                      >
                        Cancel
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
